import React, { useState, useEffect, useCallback } from 'react';
import {
  loadNodeDefinitions,
  saveNodeDefinition,
  deleteNodeDefinition,
  calculateLoss,
  toPyPSA,
  REFERENCE_VERSIONS,
} from '../store/nodeDefinitions';
import CircleNodePreview from './CircleNodePreview';

const FIELD_TYPES = ['text', 'number', 'select', 'boolean'];
const latestRef = REFERENCE_VERSIONS[REFERENCE_VERSIONS.length - 1];

/* =========================================================================
   Node categories — Electric exists, Gas and Liquid are new
   ========================================================================= */
const NODE_CATEGORIES = [
  {
    id: 'electric',
    label: 'Electric Nodes',
    color: '#F5C542',
    borderColor: '#D4A843',
    icon: '\u26A1',
    description: 'Substations, busbars, transformers. Loss parameters version-tracked.',
  },
  {
    id: 'gas',
    label: 'Gas Nodes / Junctions',
    color: '#3CC0C9',
    borderColor: '#2A9DA5',
    icon: '\uD83D\uDD25',
    description: 'Gas compressor stations, metering points, pipeline junctions.',
  },
  {
    id: 'liquid',
    label: 'Liquid Nodes / Junctions',
    color: '#7B68AE',
    borderColor: '#5F4F8B',
    icon: '\uD83D\uDCA7',
    description: 'Pump stations, tank junctions, pipeline manifolds.',
  },
];

/* =========================================================================
   Loss curve mini-chart (electric nodes only)
   ========================================================================= */
const LossCurve = ({ def }) => {
  const w = 220, h = 80;
  const pad = { t: 10, r: 10, b: 20, l: 35 };
  const plotW = w - pad.l - pad.r, plotH = h - pad.t - pad.b;
  const points = [];
  let maxLoss = 0;
  for (let pct = 0; pct <= 120; pct += 5) {
    const result = calculateLoss(def, pct / 100);
    maxLoss = Math.max(maxLoss, result.total);
    points.push({ x: pct, y: result.total });
  }
  maxLoss = Math.max(maxLoss, 0.01);
  const pathD = points.map((p, i) => {
    const x = pad.l + (p.x / 120) * plotW;
    const y = pad.t + plotH - (p.y / maxLoss) * plotH;
    return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ');

  return (
    <svg width={w} height={h} className="loss-curve">
      {[0, 0.25, 0.5, 0.75, 1].map((f) => (
        <line key={f} x1={pad.l} y1={pad.t + plotH * (1 - f)} x2={w - pad.r} y2={pad.t + plotH * (1 - f)} stroke="#1e2a4a" strokeWidth={0.5} />
      ))}
      <text x={pad.l - 4} y={pad.t + 4} textAnchor="end" fontSize="7" fill="#8a9a95" fontFamily="JetBrains Mono">{maxLoss.toFixed(2)}%</text>
      <text x={pad.l - 4} y={pad.t + plotH + 4} textAnchor="end" fontSize="7" fill="#8a9a95" fontFamily="JetBrains Mono">0%</text>
      <text x={pad.l} y={h - 2} fontSize="7" fill="#8a9a95" fontFamily="JetBrains Mono">0%</text>
      <text x={pad.l + plotW / 2} y={h - 2} textAnchor="middle" fontSize="7" fill="#8a9a95" fontFamily="JetBrains Mono">{'Load \u2192'}</text>
      <text x={w - pad.r} y={h - 2} textAnchor="end" fontSize="7" fill="#8a9a95" fontFamily="JetBrains Mono">120%</text>
      <path d={pathD} fill="none" stroke="#4ecca3" strokeWidth={1.5} />
      <line x1={pad.l} y1={pad.t + plotH - (def.fixed_loss_pct / maxLoss) * plotH} x2={w - pad.r} y2={pad.t + plotH - (def.fixed_loss_pct / maxLoss) * plotH} stroke="#f87171" strokeWidth={0.5} strokeDasharray="3 2" />
    </svg>
  );
};

/* =========================================================================
   Main Component
   ========================================================================= */
const NodeDefinitionsView = () => {
  const [allDefs, setAllDefs] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null); // null = hub view
  const [editDef, setEditDef] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [showPyPSA, setShowPyPSA] = useState(null);
  const [showRef, setShowRef] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await loadNodeDefinitions();
      setAllDefs(data);
    } catch (err) {
      console.error('Failed to load node definitions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Group definitions by category
  const defsByCategory = {};
  for (const cat of NODE_CATEGORIES) {
    defsByCategory[cat.id] = allDefs.filter(d => d.category === cat.id);
  }

  // ===== Editor helpers (same as before) =====
  const startEdit = (def) => {
    setEditDef(JSON.parse(JSON.stringify(def)));
    setDirty(false);
  };
  const updateEdit = (key, value) => { setEditDef(prev => ({ ...prev, [key]: value })); setDirty(true); };
  const updateShape = (key, value) => { setEditDef(prev => ({ ...prev, shape: { ...prev.shape, [key]: value } })); setDirty(true); };
  const toggleConnectorFav = (connId) => { setEditDef(prev => ({ ...prev, connectors: prev.connectors.map(c => c.id === connId ? { ...c, favorite: !c.favorite } : c) })); setDirty(true); };
  const toggleConnectorEnabled = (connId) => { setEditDef(prev => ({ ...prev, connectors: prev.connectors.map(c => c.id === connId ? { ...c, enabled: !c.enabled, favorite: !c.enabled ? c.favorite : false } : c) })); setDirty(true); };
  const resetToRef = () => {
    const refValues = latestRef.values[editDef.id];
    if (!refValues) return;
    setEditDef(prev => ({ ...prev, fixed_loss_pct: refValues.fixed_loss_pct, variable_loss_pct: refValues.variable_loss_pct, vk_pct: refValues.vk_pct, refVersion: latestRef.version, overridden: false }));
    setDirty(true);
  };
  const handleSave = async () => {
    const refValues = latestRef.values[editDef.id];
    if (refValues) {
      editDef.overridden = editDef.fixed_loss_pct !== refValues.fixed_loss_pct || editDef.variable_loss_pct !== refValues.variable_loss_pct;
    }
    await saveNodeDefinition(editDef);
    await refresh();
    setDirty(false);
  };
  const handleDelete = async (id) => { await deleteNodeDefinition(id); await refresh(); };
  const addField = () => {
    const id = 'f' + Date.now();
    setEditDef(prev => ({ ...prev, fields: [...prev.fields, { id, name: '', label: '', type: 'text', defaultValue: '', required: false }] }));
    setDirty(true);
  };
  const updateField = (fieldId, key, value) => { setEditDef(prev => ({ ...prev, fields: prev.fields.map(f => f.id === fieldId ? { ...f, [key]: value } : f) })); setDirty(true); };
  const removeField = (fieldId) => { setEditDef(prev => ({ ...prev, fields: prev.fields.filter(f => f.id !== fieldId) })); setDirty(true); };

  // ===================================================================
  // EDITOR VIEW (same as existing, works for any category)
  // ===================================================================
  if (editDef) {
    const refValues = latestRef.values[editDef.id];
    const hasLoss = editDef.fixed_loss_pct !== undefined;
    const loss100 = hasLoss ? calculateLoss(editDef, 1.0) : null;
    const loss50 = hasLoss ? calculateLoss(editDef, 0.5) : null;
    const pypsa = hasLoss ? toPyPSA(editDef) : null;
    const catInfo = NODE_CATEGORIES.find(c => c.id === editDef.category) || NODE_CATEGORIES[0];

    return (
      <div className="def-editor">
        <div className="def-editor-header">
          <div className="def-editor-title">
            <div className="node-color-dot" style={{ background: editDef.fillColor || catInfo.color, width: 32, height: 32, borderRadius: '50%', border: `2px solid ${editDef.strokeColor || catInfo.borderColor}` }} />
            <div>
              <h2>{editDef.name}</h2>
              <span className="def-editor-id">
                {editDef.id} &middot; {editDef.nodeType || editDef.category} &middot; {editDef.v_low !== undefined ? `${editDef.v_low}/${editDef.v_high} kV` : catInfo.label}
              </span>
            </div>
          </div>
          <div className="def-editor-actions">
            <button className="btn-ghost" onClick={() => setEditDef(null)}>Back</button>
            <button className="btn-accent" onClick={handleSave} disabled={!dirty}>{dirty ? 'Save Changes' : 'Saved'}</button>
          </div>
        </div>

        <div className="def-editor-body">
          <div className="def-editor-left">
            {/* Basic props */}
            <div className="def-section">
              <h3>Node Properties</h3>
              <div className="def-form-row">
                <div className="def-form-group"><label>Name</label><input value={editDef.name} onChange={e => updateEdit('name', e.target.value)} /></div>
                {editDef.v_low !== undefined && (
                  <>
                    <div className="def-form-group" style={{ width: 100 }}><label>V Low (kV)</label><input type="number" value={editDef.v_low} onChange={e => updateEdit('v_low', parseFloat(e.target.value) || 0)} /></div>
                    <div className="def-form-group" style={{ width: 100 }}><label>V High (kV)</label><input type="number" value={editDef.v_high} onChange={e => updateEdit('v_high', parseFloat(e.target.value) || 0)} /></div>
                  </>
                )}
              </div>
              {editDef.nodeType && (
                <div className="def-form-row">
                  <div className="def-form-group">
                    <label>Type</label>
                    <select value={editDef.nodeType} onChange={e => {
                      const isTx = e.target.value === 'transformer';
                      updateEdit('nodeType', e.target.value);
                      setEditDef(prev => ({ ...prev, hasTransformer: isTx, splitView: isTx }));
                    }}>
                      <option value="busbar">Busbar (no transformer)</option>
                      <option value="transformer">Transformer substation</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Loss parameters (electric only) */}
            {hasLoss && (
              <div className="def-section">
                <div className="def-section-header">
                  <h3>Loss Parameters</h3>
                  {refValues && <button className="btn-sm-ref" onClick={resetToRef} title="Reset to reference values">{'\u21BB'} Reset to Ref v{latestRef.version}</button>}
                </div>
                {refValues && (
                  <div className="ref-info-box">
                    <div className="ref-info-header"><span>Reference v{latestRef.version}</span>{editDef.overridden && <span className="ref-overridden">OVERRIDDEN</span>}</div>
                    <div className="ref-info-values"><span>Fixed: {refValues.fixed_loss_pct}%</span><span>Variable: {refValues.variable_loss_pct}%</span>{refValues.vk_pct && <span>vk: {refValues.vk_pct}%</span>}</div>
                    <div className="ref-info-notes">{refValues.notes}</div>
                    <div className="ref-info-source">Source: {latestRef.source}</div>
                  </div>
                )}
                <div className="def-form-row">
                  <div className="def-form-group"><label>Fixed Loss (%)</label><input type="number" step="0.001" value={editDef.fixed_loss_pct} onChange={e => updateEdit('fixed_loss_pct', parseFloat(e.target.value) || 0)} /></div>
                  <div className="def-form-group"><label>Variable Loss (% at I_rated)</label><input type="number" step="0.001" value={editDef.variable_loss_pct} onChange={e => updateEdit('variable_loss_pct', parseFloat(e.target.value) || 0)} /></div>
                  {editDef.hasTransformer && <div className="def-form-group"><label>Impedance vk (%)</label><input type="number" step="0.1" value={editDef.vk_pct || ''} onChange={e => updateEdit('vk_pct', parseFloat(e.target.value) || null)} /></div>}
                </div>
                <div className="loss-formula-box">
                  <div className="loss-formula">Loss% = {editDef.fixed_loss_pct} + {editDef.variable_loss_pct} {'×'} (I / I_rated){'\u00B2'}</div>
                  <div className="loss-examples"><span>At 50% load: <strong>{loss50.total.toFixed(4)}%</strong></span><span>At 100% load: <strong>{loss100.total.toFixed(4)}%</strong></span></div>
                  <LossCurve def={editDef} />
                </div>
                {pypsa && (
                  <div className="pypsa-box">
                    <div className="def-section-header" style={{ marginBottom: 8 }}><h3>PyPSA Parameters</h3></div>
                    <div className="pypsa-params">
                      {Object.entries(pypsa).map(([k, v]) => (
                        <div key={k} className="pypsa-param"><span className="pypsa-key">{k}</span><span className="pypsa-val">{v === null ? '(per instance)' : typeof v === 'number' ? v.toFixed(6) : String(v)}</span></div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Database fields */}
            <div className="def-section">
              <div className="def-section-header"><h3>Database Fields</h3><button className="btn-sm-accent" onClick={addField}>+ Add Field</button></div>
              <div className="def-field-list">
                {editDef.fields?.map(field => (
                  <div key={field.id} className="def-field-row">
                    <div className="def-field-inputs">
                      <input className="field-name mono" placeholder="field_name" value={field.name} onChange={e => updateField(field.id, 'name', e.target.value)} />
                      <input className="field-label" placeholder="Label" value={field.label} onChange={e => updateField(field.id, 'label', e.target.value)} />
                      <select className="field-type" value={field.type} onChange={e => updateField(field.id, 'type', e.target.value)}>{FIELD_TYPES.map(t => <option key={t}>{t}</option>)}</select>
                      <input className="field-default" placeholder={field.type === 'select' ? 'opt1, opt2, ...' : 'Default'} value={field.type === 'select' ? (field.options || []).join(', ') : field.defaultValue} onChange={e => { if (field.type === 'select') updateField(field.id, 'options', e.target.value.split(',').map(s => s.trim()).filter(Boolean)); else updateField(field.id, 'defaultValue', e.target.value); }} />
                      {field.unit !== undefined && <input className="field-unit" placeholder="unit" value={field.unit || ''} onChange={e => updateField(field.id, 'unit', e.target.value)} />}
                    </div>
                    <button className="btn-remove-field" onClick={() => removeField(field.id)}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT: Shape + Connectors */}
          <div className="def-editor-right">
            <div className="def-section">
              <h3>Shape</h3>
              <div className="def-form-row">
                <div className="def-form-group"><label>Diameter (px)</label><input type="number" value={editDef.shape?.diameter || 80} onChange={e => updateShape('diameter', parseInt(e.target.value) || 60)} min={40} max={200} /></div>
              </div>
              <div className="def-form-row">
                <div className="def-form-group">
                  <label>{editDef.splitView ? 'Low-V Fill' : 'Fill Color'}</label>
                  <div className="color-input-wrap"><input type="color" value={editDef.fillColor || '#666'} onChange={e => updateEdit('fillColor', e.target.value)} className="color-picker" /><input type="text" value={editDef.fillColor || ''} onChange={e => updateEdit('fillColor', e.target.value)} className="color-hex mono" /></div>
                </div>
                {editDef.splitView && <div className="def-form-group"><label>High-V Fill</label><div className="color-input-wrap"><input type="color" value={editDef.fillColorHigh || editDef.fillColor} onChange={e => updateEdit('fillColorHigh', e.target.value)} className="color-picker" /><input type="text" value={editDef.fillColorHigh || ''} onChange={e => updateEdit('fillColorHigh', e.target.value)} className="color-hex mono" /></div></div>}
              </div>
              <div className="def-form-row"><div className="def-form-group"><label>Stroke Color</label><div className="color-input-wrap"><input type="color" value={editDef.strokeColor || '#666'} onChange={e => updateEdit('strokeColor', e.target.value)} className="color-picker" /><input type="text" value={editDef.strokeColor || ''} onChange={e => updateEdit('strokeColor', e.target.value)} className="color-hex mono" /></div></div></div>
            </div>
            {editDef.connectors && (
              <>
                <div className="def-section">
                  <h3>Preview</h3>
                  <div className="shape-preview-container">
                    <CircleNodePreview diameter={editDef.shape?.diameter || 80} fillColor={editDef.fillColor} fillColorHigh={editDef.fillColorHigh} strokeColor={editDef.strokeColor} connectors={editDef.connectors} splitView={editDef.splitView} vLow={editDef.v_low} vHigh={editDef.v_high} name={editDef.name} interactive onConnectorClick={toggleConnectorFav} />
                  </div>
                  <div className="shape-preview-hint">Click a connector to toggle favorite.</div>
                </div>
                <div className="def-section">
                  <h3>Connectors</h3>
                  <div className="def-form-group"><label>Max Selectable on Canvas</label><input type="number" value={editDef.maxSelectableConnectors || 4} onChange={e => updateEdit('maxSelectableConnectors', parseInt(e.target.value) || 1)} min={1} max={8} /></div>
                  <div className="connector-grid-circle">
                    {editDef.connectors.map(c => (
                      <div key={c.id} className="connector-toggle-row">
                        <label className="connector-toggle"><input type="checkbox" checked={c.enabled} onChange={() => toggleConnectorEnabled(c.id)} /><span className="connector-pos">{c.label}</span></label>
                        <button className={`btn-fav ${c.favorite ? 'active' : ''}`} onClick={() => toggleConnectorFav(c.id)} disabled={!c.enabled}>{'★'}</button>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ===================================================================
  // CATEGORY LIST VIEW (electric, gas, liquid detail screen)
  // ===================================================================
  if (activeCategory) {
    const catInfo = NODE_CATEGORIES.find(c => c.id === activeCategory);
    const catDefs = defsByCategory[activeCategory] || [];

    return (
      <div className="def-list-view">
        <div className="def-list-header">
          <div>
            <h1 style={{ color: catInfo.color }}>{catInfo.icon} {catInfo.label}</h1>
            <p className="def-list-subtitle">{catInfo.description}</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-ghost" onClick={() => setActiveCategory(null)}>{'← Back to Overview'}</button>
            {activeCategory === 'electric' && (
              <button className="btn-ghost" onClick={() => setShowRef(!showRef)}>{showRef ? 'Hide' : 'Show'} Reference v{latestRef.version}</button>
            )}
          </div>
        </div>

        {/* Reference table (electric only) */}
        {showRef && activeCategory === 'electric' && (
          <div className="ref-table-container">
            <table className="user-table">
              <thead><tr><th>Node</th><th>Fixed Loss %</th><th>Variable Loss %</th><th>vk %</th><th>Notes</th></tr></thead>
              <tbody>
                {Object.entries(latestRef.values).map(([key, val]) => (
                  <tr key={key}><td className="mono">{key.replace('_', '/')}</td><td className="mono">{val.fixed_loss_pct}</td><td className="mono">{val.variable_loss_pct}</td><td className="mono">{val.vk_pct || '—'}</td><td style={{ fontSize: 11, color: '#8a9a95' }}>{val.notes}</td></tr>
                ))}
              </tbody>
            </table>
            <div style={{ fontSize: 10, color: '#4a5568', padding: '8px 12px' }}>Source: {latestRef.source} &middot; Version {latestRef.version} ({latestRef.date})</div>
          </div>
        )}

        {catDefs.length === 0 ? (
          <div className="node-hub-empty">No {catInfo.label.toLowerCase()} defined yet. This category is ready for definition.</div>
        ) : (
          <div className="def-card-grid" style={{ marginTop: 16 }}>
            {catDefs.map(def => {
              const hasLoss = def.fixed_loss_pct !== undefined;
              const loss100 = hasLoss ? calculateLoss(def, 1.0) : null;
              return (
                <div key={def.id} className="def-card" style={{ borderColor: def.strokeColor || catInfo.borderColor }}>
                  {def.connectors && (
                    <div className="def-card-preview">
                      <CircleNodePreview diameter={def.shape?.diameter || 80} fillColor={def.fillColor} fillColorHigh={def.fillColorHigh} strokeColor={def.strokeColor} connectors={def.connectors} splitView={def.splitView} vLow={def.v_low} vHigh={def.v_high} name={def.name} scale={0.7} />
                    </div>
                  )}
                  <div className="def-card-info">
                    <div className="def-card-name">{def.name}</div>
                    <div className="def-card-meta">
                      <span>{def.nodeType || def.category}</span>
                      <span>{def.fields?.length || 0} fields</span>
                      {def.connectors && <span>{def.connectors.filter(c => c.enabled).length} connectors</span>}
                    </div>
                    {hasLoss && (
                      <div className="def-card-dims mono">
                        Loss at 100%: {loss100.total.toFixed(3)}%
                        {def.overridden && <span className="ref-overridden" style={{ marginLeft: 6 }}>custom</span>}
                        {!def.overridden && <span style={{ marginLeft: 6, color: '#4ecca3', fontSize: 10 }}>ref v{def.refVersion}</span>}
                      </div>
                    )}
                  </div>
                  <div className="def-card-actions">
                    <button className="btn-card" onClick={() => startEdit(def)}>Edit</button>
                    {hasLoss && <button className="btn-card" onClick={() => setShowPyPSA(showPyPSA === def.id ? null : def.id)}>PyPSA</button>}
                    <button className="btn-card danger" onClick={() => handleDelete(def.id)}>Delete</button>
                  </div>
                  {showPyPSA === def.id && (
                    <div className="pypsa-box" style={{ margin: '0 16px 14px' }}>
                      <div className="pypsa-params">
                        {Object.entries(toPyPSA(def)).map(([k, v]) => (
                          <div key={k} className="pypsa-param"><span className="pypsa-key">{k}</span><span className="pypsa-val">{v === null ? '(per instance)' : typeof v === 'number' ? v.toFixed(6) : String(v)}</span></div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ===================================================================
  // HUB VIEW — 3 category columns
  // ===================================================================
  if (loading) {
    return <div className="def-list-view"><div className="def-list-header"><div><h1>Node Definitions</h1><p className="def-list-subtitle">Loading...</p></div></div></div>;
  }

  return (
    <div className="def-list-view">
      <div className="def-list-header">
        <div>
          <h1>Node Definitions</h1>
          <p className="def-list-subtitle">
            Define network nodes across three domains: electrical, gas, and liquid systems.
          </p>
        </div>
      </div>

      <div className="node-hub-grid">
        {NODE_CATEGORIES.map(cat => {
          const catDefs = defsByCategory[cat.id] || [];
          const busbarCount = catDefs.filter(d => d.nodeType === 'busbar' || !d.hasTransformer).length;
          const txCount = catDefs.filter(d => d.nodeType === 'transformer' || d.hasTransformer).length;

          return (
            <div key={cat.id} className="node-hub-card" style={{ borderTopColor: cat.color }}>
              {/* Header */}
              <div className="node-hub-card-header">
                <span className="node-hub-icon" style={{ background: cat.color }}>{cat.icon}</span>
                <div>
                  <div className="node-hub-card-title" style={{ color: cat.color }}>{cat.label}</div>
                  <div className="node-hub-card-desc">{cat.description}</div>
                </div>
              </div>

              {/* Stats */}
              <div className="node-hub-stats">
                <div className="node-hub-stat">
                  <span className="node-hub-stat-value" style={{ color: cat.color }}>{catDefs.length}</span>
                  <span className="node-hub-stat-label">Total</span>
                </div>
                {cat.id === 'electric' && (
                  <>
                    <div className="node-hub-stat">
                      <span className="node-hub-stat-value">{busbarCount}</span>
                      <span className="node-hub-stat-label">Busbars</span>
                    </div>
                    <div className="node-hub-stat">
                      <span className="node-hub-stat-value">{txCount}</span>
                      <span className="node-hub-stat-label">Transformers</span>
                    </div>
                  </>
                )}
                {cat.id !== 'electric' && (
                  <div className="node-hub-stat">
                    <span className="node-hub-stat-value" style={{ color: '#8a9a95' }}>{'—'}</span>
                    <span className="node-hub-stat-label">Ready to define</span>
                  </div>
                )}
              </div>

              {/* Mini list of nodes */}
              <div className="node-hub-list">
                {catDefs.length === 0 ? (
                  <div className="node-hub-empty-item">No nodes defined yet</div>
                ) : (
                  catDefs.slice(0, 6).map(def => (
                    <div key={def.id} className="node-hub-list-item" style={{ borderLeftColor: def.strokeColor || cat.borderColor }}>
                      <span className="node-hub-list-name">{def.name}</span>
                      {def.v_low !== undefined && (
                        <span className="node-hub-list-meta">{def.v_low}/{def.v_high} kV</span>
                      )}
                    </div>
                  ))
                )}
                {catDefs.length > 6 && <div className="node-hub-more">+{catDefs.length - 6} more</div>}
              </div>

              {/* Open button */}
              <button
                className="node-hub-open-btn"
                style={{ background: cat.color, color: '#1a1a2e' }}
                onClick={() => setActiveCategory(cat.id)}
              >
                Open {cat.label} {'→'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default NodeDefinitionsView;
